import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

const CUBE = 1 as LevelPieceType;
const BRICK_VARIANT = 1;
const COMMON_DIFFICULTY = 0 as LevelConfig["difficulty"];

interface BrickSpec
{
  x: number;
  bottom: number;
  width: number;
  height: number;
}

function brick( x: number, bottom: number, width = 1, height = 1 ): BrickSpec
{
  return { x, bottom, width, height };
}

function makeLayer( specs: BrickSpec[], cols: number, rows: number ): LevelLayer
{
  const grid = Array.from( { length: rows }, () => Array( cols ).fill( 0 ) );
  const pieces: LevelPiece[] = specs.map( ( spec, index ) =>
  {
    const id = index + 1;
    const y = rows - spec.bottom - spec.height;

    for ( let row = y; row < y + spec.height; row++ )
      for ( let col = spec.x; col < spec.x + spec.width; col++ )
      {
        if ( grid[row]?.[col] !== 0 )
          console.warn( `[BrickLevelsShowcase] Piece ${id} does not fit at ${col},${row}` );
        else
          grid[row][col] = id;
      }

    return { id, type: CUBE, x: spec.x, y, width: spec.width, height: spec.height, variant: BRICK_VARIANT };
  } );

  return { grid, pieces };
}

function runningBond( x: number, width: number, courses: number, bottom = 0 ): BrickSpec[]
{
  const specs: BrickSpec[] = [];
  for ( let course = 0; course < courses; course++ )
  {
    let col = x;
    if ( course % 2 === 1 )
    {
      specs.push( brick( col, bottom + course ) );
      col++;
    }
    for ( ; col + 2 <= x + width; col += 2 )
      specs.push( brick( col, bottom + course, 2, 1 ) );
    if ( col < x + width )
      specs.push( brick( col, bottom + course ) );
  }
  return specs;
}

function grid1x1( cols: number, rows: number ): BrickSpec[]
{
  const specs: BrickSpec[] = [];
  for ( let row = 0; row < rows; row++ )
    for ( let col = 0; col < cols; col++ )
      specs.push( brick( col, row ) );
  return specs;
}

const BRICK_LEVELS: Array<{ label: string; config: LevelConfig }> = [
  {
    label: "WALL: RUNNING BOND 2x1",
    config: { level: 90600, difficulty: COMMON_DIFFICULTY, layers: [ makeLayer( runningBond( 0, 8, 4 ), 8, 4 ) ] },
  },
  {
    label: "PYRAMID 1x1",
    config: {
      level: 90601,
      difficulty: COMMON_DIFFICULTY,
      layers: [ makeLayer( [
        ...[ 0, 1, 2, 3, 4, 5, 6, 7 ].map( x => brick( x, 0 ) ),
        ...[ 1, 2, 3, 4, 5, 6 ].map( x => brick( x, 1 ) ),
        ...[ 2, 3, 4, 5 ].map( x => brick( x, 2 ) ),
        ...[ 3, 4 ].map( x => brick( x, 3 ) ),
      ], 8, 4 ) ],
    },
  },
  {
    label: "STAIRS 1x1-1x5 + 2x2",
    config: {
      level: 90602,
      difficulty: COMMON_DIFFICULTY,
      layers: [ makeLayer( [
        brick( 0, 0, 1, 1 ),
        brick( 1, 0, 1, 2 ),
        brick( 2, 0, 1, 3 ),
        brick( 3, 0, 1, 4 ),
        brick( 4, 0, 1, 5 ),
        brick( 6, 0, 2, 2 ),
      ], 8, 5 ) ],
    },
  },
  {
    label: "COLONNADE: PILLARS + LINTELS",
    config: {
      level: 90603,
      difficulty: COMMON_DIFFICULTY,
      layers: [ makeLayer( [
        brick( 0, 0, 1, 3 ),
        brick( 3, 0, 1, 3 ),
        brick( 4, 0, 1, 3 ),
        brick( 7, 0, 1, 3 ),
        brick( 0, 3, 4, 1 ),
        brick( 4, 3, 4, 1 ),
        brick( 1, 4, 2, 2 ),
        brick( 5, 4, 2, 2 ),
      ], 8, 6 ) ],
    },
  },
  {
    label: "JENGA: LONG BARS 8/4/1",
    config: {
      level: 90604,
      difficulty: COMMON_DIFFICULTY,
      layers: [ makeLayer( [
        brick( 0, 0, 8, 1 ),
        ...[ 0, 3, 4, 7 ].map( x => brick( x, 1 ) ),
        brick( 0, 2, 4, 1 ),
        brick( 4, 2, 4, 1 ),
        ...[ 1, 2, 5, 6 ].map( x => brick( x, 3 ) ),
        brick( 0, 4, 8, 1 ),
        brick( 3, 5, 2, 1 ),
      ], 8, 6 ) ],
    },
  },
  {
    label: "2x2 CHECKER STACK",
    config: {
      level: 90605,
      difficulty: COMMON_DIFFICULTY,
      layers: [ makeLayer( [
        ...[ 0, 2, 4, 6 ].map( x => brick( x, 0, 2, 2 ) ),
        ...[ 1, 3, 5 ].map( x => brick( x, 2, 2, 2 ) ),
        ...[ 2, 4 ].map( x => brick( x, 4, 2, 2 ) ),
        brick( 3, 6, 2, 2 ),
      ], 8, 8 ) ],
    },
  },
  {
    label: "CASTLE: TOWERS + WALL",
    config: {
      level: 90606,
      difficulty: COMMON_DIFFICULTY,
      layers: [ makeLayer( [
        brick( 0, 0, 2, 5 ),
        brick( 6, 0, 2, 5 ),
        ...runningBond( 2, 4, 3 ),
        brick( 2, 3 ),
        brick( 5, 3 ),
        brick( 0, 5 ),
        brick( 7, 5 ),
      ], 8, 6 ) ],
    },
  },
  {
    label: "DEEP BLOCK: 3 LAYERS",
    config: {
      level: 90607,
      difficulty: COMMON_DIFFICULTY,
      layers: [
        makeLayer( runningBond( 1, 6, 3 ), 8, 3 ),
        makeLayer( runningBond( 1, 6, 3 ), 8, 3 ),
        makeLayer( runningBond( 1, 6, 3 ), 8, 3 ),
      ],
    },
  },
  {
    label: "ROUND TABLE: BRICK RING",
    config: {
      level: 90608,
      difficulty: COMMON_DIFFICULTY,
      layers: [ makeLayer( [ brick( 2, 0, 1, 2 ) ], 5, 3 ) ],
      tables: [ {
        shape: "round",
        layers: [
          makeLayer( [ brick( 2, 0, 1, 2 ) ], 5, 3 ),
          makeLayer( [ brick( 1, 0 ), brick( 2, 0, 1, 2 ), brick( 3, 0 ) ], 5, 3 ),
          makeLayer( [ brick( 0, 0, 1, 2 ), brick( 1, 0 ), brick( 2, 0, 1, 3 ), brick( 3, 0 ), brick( 4, 0, 1, 2 ) ], 5, 3 ),
          makeLayer( [ brick( 1, 0 ), brick( 2, 0, 1, 2 ), brick( 3, 0 ) ], 5, 3 ),
          makeLayer( [ brick( 2, 0, 1, 2 ) ], 5, 3 ),
        ],
      } ],
    },
  },
  {
    label: "STRESS: 80 x 1x1 (2 LAYERS)",
    config: {
      level: 90609,
      difficulty: COMMON_DIFFICULTY,
      layers: [ makeLayer( grid1x1( 8, 5 ), 8, 5 ), makeLayer( grid1x1( 8, 5 ), 8, 5 ) ],
    },
  },
];

export class BrickLevelsShowcase
{
  public static readonly TESTS = BRICK_LEVELS.map( entry => ( { label: entry.label, level: entry.config.level } ) );

  public static getConfig( level: number ): LevelConfig | null
  {
    return BRICK_LEVELS.find( entry => entry.config.level === level )?.config ?? null;
  }
}
